import * as React from 'react'

import { cn } from '../../lib/cn'

export interface MethodSwitcherOption {
  /** Stable key passed back to onSelect (e.g. the auth method id). */
  key: string
  /** Visible tile label (e.g. "Magic link"). */
  label: string
  /** Optional leading icon (a lucide icon element). */
  icon?: React.ReactNode
}

export interface MethodSwitcherProps {
  /** The secondary methods to offer as tiles. Render nothing when empty. */
  methods: MethodSwitcherOption[]
  onSelect: (key: string) => void
  /** Divider caption. Defaults to "Or use". */
  label?: string
  className?: string
}

/**
 * MethodSwitcher — the "Or use" divider plus a row of equal-width tiles for
 * switching the foregrounded auth method. Presentation only; the caller owns
 * which methods appear and what selecting one does.
 */
export const MethodSwitcher = React.forwardRef<HTMLDivElement, MethodSwitcherProps>(
  ({ methods, onSelect, label = 'Or use', className }, ref) => {
    if (methods.length === 0) return null
    return (
      <div ref={ref} className={cn('flex flex-col gap-3', className)}>
        <div className="flex items-center gap-3" aria-hidden>
          <span className="h-px flex-1 bg-ia-border" />
          <span className="text-caption tracking-[0.18em] text-ia-text-tertiary">{label}</span>
          <span className="h-px flex-1 bg-ia-border" />
        </div>
        <div className="flex gap-2">
          {methods.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => onSelect(m.key)}
              className={cn(
                'flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5 rounded-lg border border-ia-border bg-ia-input-bg px-2 py-3.5 transition-colors',
                'hover:border-ia-border-strong hover:bg-ia-surface-hover',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              {m.icon && <span className="text-ia-text-secondary [&_svg]:size-5">{m.icon}</span>}
              <span className="truncate text-body-sm font-medium text-ia-text">{m.label}</span>
            </button>
          ))}
        </div>
      </div>
    )
  },
)
MethodSwitcher.displayName = 'MethodSwitcher'
